import { ReactNode, useEffect, useRef, useState } from "react";
import { ViewerToolbar } from "./ViewerToolbar";
import { printDocument } from "../../../Utils/printDocument";

const ZOOM_STEP = 0.25;
const MIN_SCALE = 0.5;
const MAX_SCALE = 3;

type ViewerShellProps = {
  url: string;
  fileName?: string;
  nav?: ReactNode;
  isToolbarActive?: boolean;
  children: ReactNode;
};

export const ViewerShell = ({
  url,
  fileName,
  nav,
  isToolbarActive,
  children,
}: ViewerShellProps) => {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [error, setError] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);

  const zoomIn = () => setScale((s) => Math.min(MAX_SCALE, +(s + ZOOM_STEP).toFixed(2)));
  const zoomOut = () => setScale((s) => Math.max(MIN_SCALE, +(s - ZOOM_STEP).toFixed(2)));

  
  const fitToWidth = () => setScale(1);

  const toggleFullscreen = async () => {
    const node = containerRef.current;
    if (!node) return;
    try {
      if (!document.fullscreenElement) {
        await node.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch {
      setError("Fullscreen isn't available in this browser.");
    }
  };

  useEffect(() => {
    const handleChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleChange);
    return () => document.removeEventListener("fullscreenchange", handleChange);
  }, []);

  return (
    <div className="doc-viewer" ref={containerRef}>
      <ViewerToolbar
        scale={scale}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onFitWidth={fitToWidth}
        onToggleFullscreen={toggleFullscreen}
        isFullscreen={isFullscreen}
        onRotateLeft={() => setRotation((r) => (r - 90 + 360) % 360)}
        onRotateRight={() => setRotation((r) => (r + 90) % 360)}
        onPrint={() => printDocument(url, "image")}
        downloadUrl={url}
        fileName={fileName}
        nav={nav}
        isActive={isToolbarActive}
        onDownloadError={setError}
      />
      <div className="doc-viewer-stage doc-viewer-stage--scroll" ref={stageRef}>
        <div
          className="doc-viewer-rotator"
          style={{ transform: `rotate(${rotation}deg)`, width: `${scale * 100}%` }}
        >
          {children}
        </div>
      </div>
      {error && <div className="doc-viewer-error">{error}</div>}
    </div>
  );
};